import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Menu,
  Modal,
  MultiSelect,
  NumberInput,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { ISelectedGisMap } from "@mctypes/frontend.types";
import { TDimension } from "@mctypes/global.types";
import { IIssueBoardApi, IIssueViewEnum } from "@mcapps/shared";
import { IIssueApi, ITableTemplate } from "@schemas/issueSchema";
import {
  IconAlertTriangle,
  IconArchive,
  IconChevronDown,
  IconCircleCheck,
  IconLock,
  IconPencil,
  IconX,
} from "@tabler/icons-react";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ReactToPrint from "react-to-print";
import {
  CreateNewButton,
  DimensionSelector,
  ExcelExportDropdown,
  InfoCircle,
  MCLoader,
  SegmentedToggle,
} from "../../../../components";
import McDropdownNew from "../../../../components/McDropdown/MCDropdownNew";
import ToggleTextExpand from "../../../../components/ToggleTextExpand";
import { selectIsUserDev } from "../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { IssueViewEnum } from "@mcapps/shared/enums";
import { DimensionEnums } from "@mcapps/shared/sharedConstants";
import {
  PutProjectByProjectIdIssuesUpdateSimpleManyApiArg,
  usePutProjectByProjectIdIssuesArchiveManyMutation,
  usePutProjectByProjectIdIssuesUpdateSimpleManyMutation,
} from "../../../../../openapi";
import { getSelectedGisMapToLoadByDefault } from "../../../utils";
import { projectHasBotha2dAnd3dArcgisMap } from "../../admin/selectors";
import {
  ISSUE_DASHBOARD_LS_KEY,
  ISSUE_DASHBOARD_OPTIONS,
  ISSUE_KANBAN_GROUPING_LS_KEY,
  ISSUE_KANBAN_GROUPING_VIEWS,
  ISSUE_VIEWS,
  BIM_ISSUE_VIEW_PROJECT_NOS,
  IssueKanbanGroupingViewEnum,
} from "../issueConstants";
import {
  IBulkAttributeOption,
  selectBulkAttributeOptions,
  selectActiveTableTemplate,
  selectHttpIssuesOnBoard,
  selectSelectedIssueCountOnCurrentIssueBoard,
  selectSelectedIssueIdsOnCurrentIssueBoard,
} from "../issueSelectors";
import {
  clearSelectedIssueIdsInRedux,
  setActiveDashboardInRedux,
  setIssueActiveViewInRedux,
  setIssueSelectedGisMapInRedux,
  setKanbanGroupingViewInRedux,
} from "../issueSlice";
import { getIssueBoardIdFromUrl, getIssueRegisterNameOption, getIssueUrlPath } from "../issueUtils";
import AddIssueModal from "./AddIssueModal";
import IssueApiButton from "./IssueApiButton";
import IssueBoardTemplates from "./IssueBoardTemplates/IssueBoardTemplates";
import IssueDocxTemplates from "./IssueDocxTemplates";

const IssueTabActionBar: React.FC<{
  setActiveTab: (tab: string) => void;
  issueBoard: IIssueBoardApi;
  setTableByTableTemplateCb: (template: ITableTemplate) => void;
  gridRef: React.RefObject<AgGridReact>;
  isLoading: boolean;
}> = ({ setActiveTab, issueBoard, setTableByTableTemplateCb, gridRef, isLoading }) => {
  const [addIssueModalOpen, setAddIssueModalOpen] = useState(false);
  const history = useHistory();

  const issueBoardId = getIssueBoardIdFromUrl(history.location.pathname);

  const dispatch = useAppDispatch();

  const project = useAppSelector((state) => state.adminReducer.project);

  const showDimensionSelector = useSelector(projectHasBotha2dAnd3dArcgisMap);

  const activeView = useAppSelector((state) => state.issueReducerNew.activeView);
  const activeTemplate = useAppSelector(selectActiveTableTemplate);
  const kanbanGroupingView = useAppSelector((state) => state.issueReducerNew.kanbanGroupingView);
  const selectedGisMap = useAppSelector((state) => state.issueReducerNew.selectedGisMap);
  const activeDashboard = useAppSelector((state) => state.issueReducerNew.activeDashboard);
  const isTaskActive: boolean = useAppSelector((s) => s.issueReducerNew.taskAndOrIssueArgs.includes("TASK"));
  const isUserDev = useAppSelector(selectIsUserDev);
  const showBimIssueView = projectNoHasBimIssueView(project?.maconomy?.projectNo);

  React.useEffect(() => {
    if (selectedGisMap == null && project.arcgis?.groupId != null) {
      const _selectedGisMap = getSelectedGisMapToLoadByDefault(project.arcgis);

      dispatch(setIssueSelectedGisMapInRedux(_selectedGisMap));
    }
  }, []);

  const setGisDimension = (dimension: TDimension) => {
    let _selectedGisMap: ISelectedGisMap = {
      dimension: null,
      mapId: null,
    };
    if (dimension === DimensionEnums.TWODIMENSION) {
      _selectedGisMap = {
        dimension: DimensionEnums.TWODIMENSION,
        mapId: project.arcgis?.default2dMap,
      };
    }
    if (dimension === DimensionEnums.THEREEDIMENSION) {
      _selectedGisMap = {
        dimension: DimensionEnums.THEREEDIMENSION,
        mapId: project.arcgis?.default3dMap,
      };
    }
    dispatch(setIssueSelectedGisMapInRedux(_selectedGisMap));
  };

  const addIssueAndCloseModalAndOpenIssue = (issue: IIssueApi) => {
    setAddIssueModalOpen(false);
    history.push(getIssueUrlPath(history.location.pathname, issue._id));
  };

  const goToSettings = () => {
    setActiveTab("settings");
  };

  useEffect(() => {
    if (issueBoard != null) {
      const kanbanViewSetting = localStorage.getItem(
        `${ISSUE_KANBAN_GROUPING_LS_KEY}_${issueBoardId}`,
      ) as IssueKanbanGroupingViewEnum;

      const viewMode = determineKanbanGroupingView(issueBoard, kanbanViewSetting);
      dispatch(setKanbanGroupingViewInRedux(viewMode));
    }
  }, [issueBoard]);

  useEffect(() => {
    if (activeView === IssueViewEnum.BIM && !showBimIssueView) {
      dispatch(setIssueActiveViewInRedux(IssueViewEnum.TABLE));
    }
  }, [activeView, dispatch, showBimIssueView]);

  const excelExportColumns = (allColumns: boolean) => {
    let fileprefix = getIssueRegisterNameOption(issueBoard?.issueNameSetting, "plural") ?? "Issues";
    fileprefix = fileprefix.charAt(0).toUpperCase() + fileprefix.slice(1); //capitalize first letter
    const fileName = `${fileprefix}_${JSON.stringify(new Date())}.xlsx`;
    const columns = gridRef.current?.api
      .getColumns()
      .filter(
        (column) =>
          column.getColDef().type !== "metaColumn" &&
          column.getColDef().type !== "alwaysHidden" &&
          column.getColDef().context !== "METACOLUMN",
      );
    const filteredColumns = allColumns ? columns : columns.filter((column) => column.isVisible());
    if (filteredColumns.length < 1) return;
    gridRef.current!.api.exportDataAsExcel({
      fileName,
      sheetName: "Data",
      columnKeys: filteredColumns.map((col) => col.getColId()),
    });
  };

  const exportExcelAllColumns = () => {
    excelExportColumns(true);
  };

  const excelExportVisibleColumns = () => {
    excelExportColumns(false);
  };

  return (
    <Box w="100%" p="xxs">
      <Group align="center" gap="xs" wrap="nowrap" w="100%">
        <AddIssueModal
          toggle={() => setAddIssueModalOpen(false)}
          isOpen={addIssueModalOpen}
          issueBoardId={issueBoardId}
          onCreateItem={addIssueAndCloseModalAndOpenIssue}
          currentView={activeView}
        />
        <CreateNewButton
          size={"sm"}
          onClick={() => setAddIssueModalOpen(true)}
          tooltip={`Opprett ${getIssueRegisterNameOption(issueBoard?.issueNameSetting, "singular")?.toLowerCase()}`}
          disabled={activeView === "GIS"}
        />
        {activeView === "GIS" && <InfoCircle text="Klikk på kartet for å registrere" />}
        <IssueBoardTemplates gridRef={gridRef} />
        <IssueDocxTemplates gridRef={gridRef} />
        {isUserDev === true && activeView === IssueViewEnum.TABLE && (
          <SelectedIssuesActionsMenu
            disableDeleteIssue={issueBoard?.disableDeleteIssue === true}
            issueBoardId={issueBoardId}
          />
        )}
        {isLoading === true && <MCLoader size={7} />} <Box flex={1} />
        {activeView === IssueViewEnum.TABLE && (
          <Group gap="xs" wrap="nowrap">
            <ExcelExportDropdown
              excelExportVisibleColumns={excelExportVisibleColumns}
              exportExcelAllColumns={exportExcelAllColumns}
            />

            <ReactToPrint
              trigger={() => (
                <Tooltip label="Tabellutskrift" withArrow offset={2} openDelay={200}>
                  <Button color={"mc-primary"} px="sm" variant="light" size="sm" id="tabellutskrift">
                    <i className="fa fa-print fa-fw" />
                  </Button>
                </Tooltip>
              )}
              content={() => document.querySelector<HTMLElement>("#myGrid")}
              onBeforeGetContent={() => {
                gridRef?.current?.api.setSideBarVisible(false);
              }}
              onAfterPrint={() => {
                gridRef?.current?.api.setSideBarVisible(true);
              }}
            />
            <ToggleTextExpand currentApp={"issues"} />
          </Group>
        )}
        {activeView === IssueViewEnum.KANBAN && (
          <Group gap="xs" align="center" wrap="nowrap">
            {kanbanGroupingView === IssueKanbanGroupingViewEnum.BUCKET && isTaskActive === true && (
              <Text
                size="xs"
                fw={700}
                px={6}
                py={4}
                style={{
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  border: "1px solid #ffeeba",
                  borderRadius: 4,
                }}
              >
                Aksjoner vises ikke for buckets
              </Text>
            )}
            <Text size="sm" c="dimmed" fw={600}>
              Gruppering
            </Text>
            <SegmentedControl
              aria-label="Velg grupperingsvisning"
              color="mc-primary"
              value={kanbanGroupingView}
              onChange={(val) => {
                localStorage.setItem(`${ISSUE_KANBAN_GROUPING_LS_KEY}_${issueBoardId}`, val);
                dispatch(setKanbanGroupingViewInRedux(val as IssueKanbanGroupingViewEnum));
              }}
              data={ISSUE_KANBAN_GROUPING_VIEWS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
          </Group>
        )}
        {activeView === IssueViewEnum.GIS && selectedGisMap != null && showDimensionSelector === true && (
          <DimensionSelector
            activeDimension={selectedGisMap.dimension}
            setActiveDimension={(dimension) => setGisDimension(dimension)}
          />
        )}
        {activeView === IssueViewEnum.DASHBOARD && (
          <>
            <McDropdownNew
              color="mc-blue"
              currentValue={activeDashboard != null ? activeDashboard : issueBoard?.dashboardSetting}
              options={ISSUE_DASHBOARD_OPTIONS}
              onChange={(_, newValue) => {
                localStorage.setItem(`${ISSUE_DASHBOARD_LS_KEY}_${issueBoardId}`, newValue);
                dispatch(setActiveDashboardInRedux(newValue));
              }}
            />
          </>
        )}
        <ViewToggler
          activeView={activeView}
          showBimIssueView={showBimIssueView}
          setActiveView={(view) => dispatch(setIssueActiveViewInRedux(view))}
        />
        <IssueApiButton />
      </Group>
    </Box>
  );
};

export default IssueTabActionBar;

type IBulkUpdateProperty = PutProjectByProjectIdIssuesUpdateSimpleManyApiArg["body"]["property"];
type IBulkUpdateLeafValue = string | number | boolean | null | (string | number | boolean | null)[];
type IBulkUpdatePreviewRecord = {
  [key: string]: IBulkUpdateLeafValue | { [key: string]: IBulkUpdateLeafValue } | undefined | null;
};
type IIssuePreviewValue = IBulkUpdateLeafValue | { [key: string]: IIssuePreviewValue } | undefined | null;
type IIssuePreviewRecord = { [key: string]: IIssuePreviewValue };
const isBulkUpdateLeafValue = (
  value: IBulkUpdateLeafValue | { [key: string]: IBulkUpdateLeafValue } | IIssuePreviewValue,
): value is IBulkUpdateLeafValue | undefined | null =>
  value == null || Array.isArray(value) || typeof value !== "object";

const SelectedIssuesActionsMenu: React.FC<{
  disableDeleteIssue: boolean;
  issueBoardId: string;
}> = ({ disableDeleteIssue, issueBoardId }) => {
  const dispatch = useAppDispatch();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [updateConfirmed, setUpdateConfirmed] = useState(false);
  const [lockedAttributeUnlocked, setLockedAttributeUnlocked] = useState(false);
  const [selectedAttributeKey, setSelectedAttributeKey] = useState<string | null>(null);
  const [textValue, setTextValue] = useState("");
  const [numberValue, setNumberValue] = useState<number | string>("");
  const [booleanValue, setBooleanValue] = useState(false);
  const [multiValue, setMultiValue] = useState<string[]>([]);
  const [archiveManyIssues, { isLoading: isArchivingMany }] = usePutProjectByProjectIdIssuesArchiveManyMutation();
  const [updateManyIssues, { isLoading: isUpdatingMany }] = usePutProjectByProjectIdIssuesUpdateSimpleManyMutation();
  const projectId = useAppSelector((state) => state.globalReducer.currentProjectId);
  const issuesOnBoard = useAppSelector(selectHttpIssuesOnBoard);
  const selectedIssueIds = useAppSelector(selectSelectedIssueIdsOnCurrentIssueBoard);
  const selectedIssueCount = useAppSelector(selectSelectedIssueCountOnCurrentIssueBoard);
  const hasSelectedIssues = selectedIssueCount > 0;
  const issueByIdForPreview = React.useMemo(
    () => new Map(issuesOnBoard.map((issue) => [issue._id, issue])),
    [issuesOnBoard],
  );
  const issuesToDelete = React.useMemo(() => {
    return selectedIssueIds.map((issueId) => ({
      _id: issueId,
      id: issueByIdForPreview.get(issueId)?.id,
      title: issueByIdForPreview.get(issueId)?.title ?? "",
    }));
  }, [issueByIdForPreview, selectedIssueIds]);

  const bulkAttributeOptions = useAppSelector(selectBulkAttributeOptions);

  const bulkAttributeSelectData = React.useMemo(() => {
    const sections = Array.from(new Set(bulkAttributeOptions.map((option) => option.section)));

    return sections.map((section) => ({
      group: section,
      items: bulkAttributeOptions
        .filter((option) => option.section === section)
        .map((option) => ({
          value: option.key,
          label: option.locked === true ? `${option.label} (låst)` : option.label,
        })),
    }));
  }, [bulkAttributeOptions]);

  const selectedAttribute = React.useMemo(
    () => bulkAttributeOptions.find((option) => option.key === selectedAttributeKey) ?? null,
    [bulkAttributeOptions, selectedAttributeKey],
  );

  const bulkUpdateProperty = React.useMemo(
    () => buildBulkUpdateProperty(),
    [booleanValue, multiValue, numberValue, selectedAttribute, textValue],
  );

  const getBulkUpdatePreviewValue = (
    property: IBulkUpdateProperty,
    attribute: IBulkAttributeOption,
  ): IBulkUpdateLeafValue | undefined => {
    if (attribute.key.startsWith("otherAttributesData.")) {
      const otherAttributeKey = attribute.key.replace("otherAttributesData.", "");
      return property.otherAttributesData?.[otherAttributeKey] ?? undefined;
    }

    const propertyRecord = property as IBulkUpdatePreviewRecord;
    if (attribute.key.includes(".")) {
      const [parentKey, childKey] = attribute.key.split(".");
      const parentValue = propertyRecord[parentKey];
      if (parentValue == null || Array.isArray(parentValue) || typeof parentValue !== "object") {
        return undefined;
      }

      const childValue = parentValue[childKey];
      return isBulkUpdateLeafValue(childValue) ? childValue ?? undefined : undefined;
    }

    const value = propertyRecord[attribute.key];
    return isBulkUpdateLeafValue(value) ? value ?? undefined : undefined;
  };

  const getIssueValueByPath = (issue: IIssueApi, path: string): IBulkUpdateLeafValue | undefined => {
    const pathParts = path.split(".");
    let currentValue: IIssuePreviewValue = issue as IIssuePreviewRecord;

    for (const key of pathParts) {
      if (currentValue == null || Array.isArray(currentValue) || typeof currentValue !== "object") {
        return undefined;
      }

      currentValue = currentValue[key];
    }

    return isBulkUpdateLeafValue(currentValue) ? currentValue ?? undefined : undefined;
  };

  const formatBulkValue = (value: IBulkUpdateLeafValue | undefined): string => {
    if (Array.isArray(value)) {
      return value.map((item) => formatBulkValue(item)).join(", ");
    }

    if (value === true) {
      return "Ja";
    }

    if (value === false) {
      return "Nei";
    }

    if (value == null || value === "") {
      return "-";
    }

    return String(value);
  };

  const BulkValueBadge: React.FC<{ color: "gray" | "blue"; value: string }> = ({ color, value }) => (
    <Badge color={color} radius="sm" size="lg" variant="light" tt="none">
      {value}
    </Badge>
  );

  const newValuePreview = React.useMemo(() => {
    if (selectedAttribute == null || bulkUpdateProperty == null) {
      return "-";
    }

    return formatBulkValue(getBulkUpdatePreviewValue(bulkUpdateProperty, selectedAttribute));
  }, [bulkUpdateProperty, selectedAttribute]);
  const selectedLockedAttributeNeedsUnlock = selectedAttribute?.locked === true && lockedAttributeUnlocked !== true;

  const closeDeleteModal = () => {
    setDeleteConfirmed(false);
    setDeleteModalOpen(false);
  };

  const closeUpdateModal = () => {
    setUpdateModalOpen(false);
    setUpdateConfirmed(false);
    setLockedAttributeUnlocked(false);
    setSelectedAttributeKey(null);
    setTextValue("");
    setNumberValue("");
    setBooleanValue(false);
    setMultiValue([]);
  };

  function buildBulkUpdateProperty(): IBulkUpdateProperty | null {
    if (selectedAttribute == null) {
      return null;
    }

    let value: string | number | boolean | null | (string | number | boolean | null)[];
    if (selectedAttribute.kind === "boolean") {
      value = booleanValue;
    } else if (selectedAttribute.kind === "number") {
      if (numberValue === "" || numberValue == null) {
        return null;
      }
      value = Number(numberValue);
      if (Number.isNaN(value)) {
        return null;
      }
    } else if (selectedAttribute.kind === "multiSelect") {
      value =
        selectedAttribute.dropdownOptionType === "NUMBER"
          ? multiValue.map((item) => Number(item)).filter((item) => !Number.isNaN(item))
          : multiValue;
    } else {
      if (textValue === "") {
        return null;
      }
      value = selectedAttribute.dropdownOptionType === "NUMBER" ? Number(textValue) : textValue;
      if (typeof value === "number" && Number.isNaN(value)) {
        return null;
      }
    }

    if (selectedAttribute.key.startsWith("otherAttributesData.")) {
      const otherAttributeKey = selectedAttribute.key.replace("otherAttributesData.", "");
      return {
        otherAttributesData: {
          [otherAttributeKey]: value,
        },
      };
    }

    if (selectedAttribute.key.includes(".")) {
      const [parentKey, childKey] = selectedAttribute.key.split(".");
      return {
        [parentKey]: {
          [childKey]: value,
        },
      } as IBulkUpdateProperty;
    }

    return {
      [selectedAttribute.key]: value,
    } as IBulkUpdateProperty;
  }

  const updateSelectedIssues = async () => {
    const property = bulkUpdateProperty;
    if (projectId == null || property == null || selectedIssueIds.length === 0) {
      return;
    }

    try {
      await updateManyIssues({
        projectId,
        body: {
          issueBoardId,
          issueIds: selectedIssueIds,
          property,
        },
      }).unwrap();
      closeUpdateModal();
    } catch {
      // Error notification is handled by the endpoint enhancement.
    }
  };

  const archiveSelectedIssues = async () => {
    if (projectId == null || selectedIssueIds.length === 0) {
      return;
    }

    try {
      await archiveManyIssues({
        projectId,
        body: {
          issueBoardId,
          issueIds: selectedIssueIds,
        },
      }).unwrap();
      closeDeleteModal();
    } catch {
      // Error notification is handled by the endpoint enhancement.
    }
  };

  if (!hasSelectedIssues) {
    return null;
  }

  return (
    <>
      <Modal
        opened={updateModalOpen}
        onClose={closeUpdateModal}
        title="Endre attributt på valgte saker"
        size="80vw"
        centered
      >
        <Stack gap="md">
          <Alert color="yellow" icon={<IconAlertTriangle size={18} />} variant="light">
            <Text fw={700}>Dette endrer {selectedIssueCount} valgte saker.</Text>
            <Text size="sm">Velg ett attributt og verdien som skal settes på alle valgte saker.</Text>
          </Alert>
          <Select
            label="Attributt"
            placeholder="Velg attributt"
            data={bulkAttributeSelectData}
            value={selectedAttributeKey}
            onChange={(value) => {
              setSelectedAttributeKey(value);
              setTextValue("");
              setNumberValue("");
              setBooleanValue(false);
              setMultiValue([]);
              setUpdateConfirmed(false);
              setLockedAttributeUnlocked(false);
            }}
            searchable
          />
          {selectedAttribute?.locked === true && (
            <Alert color="yellow" icon={<IconLock size={18} />} variant="light">
              <Stack gap="xs">
                <Text size="sm">
                  Dette attributtet er låst. Lås det opp her dersom du likevel vil batch-redigere verdien.
                </Text>
                <Checkbox
                  checked={lockedAttributeUnlocked}
                  onChange={(event) => {
                    setLockedAttributeUnlocked(event.currentTarget.checked);
                    setUpdateConfirmed(false);
                  }}
                  label="Lås opp for batch-redigering"
                />
              </Stack>
            </Alert>
          )}
          {selectedAttribute?.kind === "select" && (
            <Select
              label="Ny verdi"
              placeholder="Velg verdi"
              data={selectedAttribute.options ?? []}
              disabled={selectedLockedAttributeNeedsUnlock}
              value={textValue}
              onChange={(value) => {
                setTextValue(value ?? "");
                setUpdateConfirmed(false);
              }}
              searchable
            />
          )}
          {selectedAttribute?.kind === "multiSelect" && (
            <MultiSelect
              label="Ny verdi"
              placeholder="Velg verdier"
              data={selectedAttribute.options ?? []}
              disabled={selectedLockedAttributeNeedsUnlock}
              value={multiValue}
              onChange={(value) => {
                setMultiValue(value);
                setUpdateConfirmed(false);
              }}
              searchable
            />
          )}
          {selectedAttribute?.kind === "text" && (
            <TextInput
              label="Ny verdi"
              disabled={selectedLockedAttributeNeedsUnlock}
              value={textValue}
              onChange={(event) => {
                setTextValue(event.currentTarget.value);
                setUpdateConfirmed(false);
              }}
            />
          )}
          {selectedAttribute?.kind === "date" && (
            <TextInput
              label="Ny verdi"
              type="date"
              disabled={selectedLockedAttributeNeedsUnlock}
              value={textValue}
              onChange={(event) => {
                setTextValue(event.currentTarget.value);
                setUpdateConfirmed(false);
              }}
            />
          )}
          {selectedAttribute?.kind === "number" && (
            <NumberInput
              label="Ny verdi"
              disabled={selectedLockedAttributeNeedsUnlock}
              value={numberValue}
              onChange={(value) => {
                setNumberValue(value);
                setUpdateConfirmed(false);
              }}
              decimalScale={4}
            />
          )}
          {selectedAttribute?.kind === "boolean" && (
            <Switch
              checked={booleanValue}
              disabled={selectedLockedAttributeNeedsUnlock}
              label={booleanValue ? "Ja" : "Nei"}
              onChange={(event) => {
                setBooleanValue(event.currentTarget.checked);
                setUpdateConfirmed(false);
              }}
            />
          )}
          <ScrollArea.Autosize mah="42vh" offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={110}>ID</Table.Th>
                  <Table.Th>Tittel</Table.Th>
                  <Table.Th>Gammel verdi</Table.Th>
                  <Table.Th>Ny verdi</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {issuesToDelete.map((issue) => (
                  <Table.Tr key={issue._id}>
                    <Table.Td>{issue.id ?? issue._id}</Table.Td>
                    <Table.Td>{issue.title || "-"}</Table.Td>
                    <Table.Td>
                      <BulkValueBadge
                        color="gray"
                        value={
                          selectedAttribute == null
                            ? "-"
                            : formatBulkValue(
                                getIssueValueByPath(
                                  issueByIdForPreview.get(issue._id) ?? ({} as IIssueApi),
                                  selectedAttribute.key,
                                ),
                              )
                        }
                      />
                    </Table.Td>
                    <Table.Td>
                      <BulkValueBadge color="blue" value={newValuePreview} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
          <Checkbox
            checked={updateConfirmed}
            onChange={(event) => setUpdateConfirmed(event.currentTarget.checked)}
            label="Jeg forstår at verdien blir endret på alle valgte saker"
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeUpdateModal} disabled={isUpdatingMany}>
              Avbryt
            </Button>
            <Button
              color="mc-primary"
              disabled={
                bulkUpdateProperty == null ||
                !hasSelectedIssues ||
                !updateConfirmed ||
                selectedLockedAttributeNeedsUnlock
              }
              leftSection={<IconPencil size={16} />}
              loading={isUpdatingMany}
              onClick={updateSelectedIssues}
            >
              Endre {selectedIssueCount} valgte
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={deleteModalOpen} onClose={closeDeleteModal} title="Arkiver valgte saker" size="lg" centered>
        <Stack gap="md">
          <Alert color="red" icon={<IconAlertTriangle size={18} />} variant="light">
            <Text fw={700}>Dette arkiverer {selectedIssueCount} valgte saker.</Text>
            <Text size="sm">
              Sakene arkiveres og fjernes fra gjeldende tabellvisning. Kontroller listen før du fortsetter.
            </Text>
          </Alert>
          <ScrollArea.Autosize mah={280} offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={110}>ID</Table.Th>
                  <Table.Th>Tittel</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {issuesToDelete.map((issue) => (
                  <Table.Tr key={issue._id}>
                    <Table.Td>{issue.id ?? issue._id}</Table.Td>
                    <Table.Td>{issue.title || "-"}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
          <Checkbox
            checked={deleteConfirmed}
            onChange={(event) => setDeleteConfirmed(event.currentTarget.checked)}
            label="Jeg forstår at de valgte sakene blir arkivert"
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeleteModal} disabled={isArchivingMany}>
              Avbryt
            </Button>
            <Button
              color="red"
              disabled={!deleteConfirmed || !hasSelectedIssues}
              leftSection={<IconArchive size={16} />}
              loading={isArchivingMany}
              onClick={archiveSelectedIssues}
            >
              Arkiver {selectedIssueCount} valgte
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Menu position="bottom-start" withArrow shadow="md" disabled={!hasSelectedIssues}>
        <Menu.Target>
          <Button
            color="mc-primary"
            leftSection={<IconCircleCheck size={18} />}
            rightSection={<IconChevronDown size={16} />}
            size="md"
            variant="filled"
            px="lg"
            fw={700}
          >
            {selectedIssueCount} valgt
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Valgte saker</Menu.Label>
          <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => setUpdateModalOpen(true)}>
            Endre attributter
          </Menu.Item>
          <Menu.Item
            color="red"
            disabled={disableDeleteIssue || isArchivingMany}
            leftSection={<IconArchive size={16} />}
            onClick={() => setDeleteModalOpen(true)}
          >
            Arkiver valgte
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconX size={16} />}
            onClick={() => dispatch(clearSelectedIssueIdsInRedux({ issueBoardId }))}
          >
            Fjern valg
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  );
};

const ViewToggler: React.FC<{
  activeView: IIssueViewEnum;
  showBimIssueView: boolean;
  setActiveView: (view: IIssueViewEnum) => void;
}> = ({ activeView, showBimIssueView, setActiveView }) => {
  const newViewVisibleByKey = useNewIssueViewVisibility();
  const issueViews = ISSUE_VIEWS.filter((view) => view.key !== IssueViewEnum.BIM || showBimIssueView);
  const data = issueViews.map((view) => ({
    value: view.key,
    label: (
      <Center inline style={{ gap: 4 }}>
        <i className={`fa fa-${view.icon} fa-fw"`} />
        <span>{view.label}</span>
        {newViewVisibleByKey[view.key] === true && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 18,
              padding: "0 6px",
              borderRadius: 9,
              background: "var(--mantine-color-yellow-4)",
              color: "var(--mantine-color-dark-8)",
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            Ny
          </span>
        )}
      </Center>
    ),
  }));

  return (
    <SegmentedControl
      style={{ height: "fit-content" }}
      value={activeView}
      color="mc-primary"
      onChange={(val) => setActiveView(val as IssueViewEnum)}
      data={data}
    />
  );
};

const projectNoHasBimIssueView = (projectNo?: number): boolean =>
  projectNo != null && BIM_ISSUE_VIEW_PROJECT_NOS.some((enabledProjectNo) => enabledProjectNo === projectNo);

const NEW_ISSUE_VIEW_VISIBLE_DAYS = 15;
const NEW_ISSUE_VIEW_VISIBLE_MS = NEW_ISSUE_VIEW_VISIBLE_DAYS * 24 * 60 * 60 * 1000;
const getNewIssueViewSeenStorageKey = (viewKey: IIssueViewEnum) => `issueView:${viewKey}:firstSeenAsNew`;

const useNewIssueViewVisibility = (): Partial<Record<IIssueViewEnum, boolean>> => {
  const [visibleByKey, setVisibleByKey] = useState<Partial<Record<IIssueViewEnum, boolean>>>({});

  useEffect(() => {
    const now = Date.now();
    const nextVisibleByKey: Partial<Record<IIssueViewEnum, boolean>> = {};

    for (const view of ISSUE_VIEWS) {
      if (view.showAsNew !== true) continue;

      const storageKey = getNewIssueViewSeenStorageKey(view.key);
      const storedFirstSeen = localStorage.getItem(storageKey);
      const firstSeen = storedFirstSeen != null ? Number(storedFirstSeen) : now;

      if (storedFirstSeen == null || !Number.isFinite(firstSeen)) {
        localStorage.setItem(storageKey, String(now));
      }

      nextVisibleByKey[view.key] = now - firstSeen < NEW_ISSUE_VIEW_VISIBLE_MS;
    }

    setVisibleByKey(nextVisibleByKey);
  }, []);

  return visibleByKey;
};

const determineKanbanGroupingView = (
  board: IIssueBoardApi,
  localStorageValue: IssueKanbanGroupingViewEnum,
): IssueKanbanGroupingViewEnum => {
  if (board?.useBucketsAsWorkflow === true) {
    return IssueKanbanGroupingViewEnum.BUCKET;
  }

  if (localStorageValue != null) {
    const enumValues = Object.values(IssueKanbanGroupingViewEnum);

    if (enumValues.includes(localStorageValue)) {
      return localStorageValue;
    }
  }

  if (board?.buckets.length > 1) {
    return IssueKanbanGroupingViewEnum.BUCKET;
  }

  return IssueKanbanGroupingViewEnum.STATUS;
};
